
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

declare global {
  interface Window { _loadGTM?: () => void; _gtmLoaded?: boolean; }
}

interface CookiePrefs {
  analytics: boolean;
  marketing: boolean;
  personalization: boolean;
}

const defaultPrefs: CookiePrefs = { analytics: true, marketing: true, personalization: true };

function applyConsent(prefs: CookiePrefs | 'all' | 'essential') {
  const granted = prefs === 'all';
  const analytics = granted || (typeof prefs === 'object' && prefs.analytics);
  const marketing = granted || (typeof prefs === 'object' && prefs.marketing);

  window.dataLayer = window.dataLayer || [];
  function gtag(..._args: any[]) { window.dataLayer.push(arguments); }
  gtag('consent', 'update', {
    analytics_storage:    analytics  ? 'granted' : 'denied',
    ad_storage:           marketing  ? 'granted' : 'denied',
    ad_user_data:         marketing  ? 'granted' : 'denied',
    ad_personalization:   marketing  ? 'granted' : 'denied',
  });
}

// ── Toggle switch ─────────────────────────────────────────────────────────────
const Toggle: React.FC<{ checked: boolean; disabled?: boolean; onChange?: (v: boolean) => void }> = ({ checked, disabled, onChange }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    disabled={disabled}
    onClick={() => !disabled && onChange?.(!checked)}
    className={`relative inline-flex h-6 w-11 shrink-0 rounded-full border-2 transition-colors duration-200 focus:outline-none
      ${disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}
      ${checked ? 'bg-gold border-gold' : 'bg-dark-surface border-white/20'}`}
  >
    <span className={`pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow-sm transform transition-transform duration-200 my-auto
      ${checked ? 'translate-x-5' : 'translate-x-0.5'}`}
    />
  </button>
);

// ── Category row ─────────────────────────────────────────────────────────────
const CategoryRow: React.FC<{
  title: string;
  description: string;
  checked: boolean;
  disabled?: boolean;
  onChange?: (v: boolean) => void;
}> = ({ title, description, checked, disabled, onChange }) => (
  <div className="flex items-start gap-4 py-4 border-b border-white/8 last:border-0">
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2 mb-1">
        <span className="text-sm font-medium text-light-primary">{title}</span>
        {disabled && <span className="text-[10px] text-gold/70 border border-gold/30 rounded px-1.5 py-0.5 uppercase tracking-wide">Sempre ativo</span>}
      </div>
      <p className="text-xs text-light-muted leading-relaxed">{description}</p>
    </div>
    <div className="pt-0.5 shrink-0">
      <Toggle checked={checked} disabled={disabled} onChange={onChange} />
    </div>
  </div>
);

export const CookieConsent: React.FC = () => {
  const [visible, setVisible] = useState(false);
  const [showReview, setShowReview] = useState(false);
  const [prefs, setPrefs] = useState<CookiePrefs>({ ...defaultPrefs });

  useEffect(() => {
    if (!localStorage.getItem('cookie-consent')) {
      const t = setTimeout(() => setVisible(true), 1500);
      return () => clearTimeout(t);
    }
  }, []);

  const acceptAll = () => {
    localStorage.setItem('cookie-consent', JSON.stringify({ essential: true, ...defaultPrefs }));
    applyConsent('all');
    window._loadGTM?.();
    setVisible(false);
    setShowReview(false);
  };

  const savePrefs = () => {
    localStorage.setItem('cookie-consent', JSON.stringify({ essential: true, ...prefs }));
    applyConsent(prefs);
    if (prefs.analytics || prefs.marketing) window._loadGTM?.();
    setVisible(false);
    setShowReview(false);
  };

  const toggle = (key: keyof CookiePrefs) =>
    setPrefs(p => ({ ...p, [key]: !p[key] }));

  if (!visible) return null;

  return (
    <>
      {/* ── Banner ── */}
      {!showReview && (
        <div role="region" aria-label="Gerenciamento de cookies" className="fixed bottom-0 left-0 right-0 z-[60] p-3 sm:p-4 pointer-events-none">
          <div className="max-w-3xl mx-auto bg-dark-elevated/97 backdrop-blur-xl border border-white/10 rounded-2xl shadow-2xl pointer-events-auto">
            <div className="p-5 sm:p-6">
              <div className="flex flex-col md:flex-row md:items-center gap-4">
                <div className="flex-1">
                  <p className="text-sm font-semibold text-light-primary mb-1.5">Gerenciamento de Cookies</p>
                  <p className="text-xs text-light-muted leading-relaxed">
                    Utilizamos cookies para personalizar sua experiência, analisar o tráfego e exibir anúncios relevantes.
                    Ao continuar, você concorda com nossa{' '}
                    <Link to="/politica-de-privacidade" className="text-gold hover:underline" onClick={() => setVisible(false)}>
                      Política de Privacidade
                    </Link>
                    {' '}e{' '}
                    <Link to="/termos-de-uso" className="text-gold hover:underline" onClick={() => setVisible(false)}>
                      Termos de Uso
                    </Link>.
                  </p>
                </div>
                <div className="flex gap-2 shrink-0">
                  <button
                    onClick={() => setShowReview(true)}
                    className="px-4 py-2 text-xs font-medium text-light-muted border border-white/20 rounded-xl hover:bg-white/5 hover:text-light-primary transition-all"
                  >
                    Revisar
                  </button>
                  <button
                    onClick={acceptAll}
                    className="px-5 py-2 text-xs font-semibold bg-gold text-dark-base rounded-xl hover:bg-gold/90 transition-all"
                  >
                    Aceitar todos
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Review modal ── */}
      {showReview && (
        <div className="fixed inset-0 z-[61] flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm p-0 sm:p-4">
          <div className="w-full sm:max-w-lg bg-dark-elevated border border-white/10 rounded-t-2xl sm:rounded-2xl shadow-2xl max-h-[90dvh] flex flex-col">

            {/* Header */}
            <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-white/8 shrink-0">
              <div>
                <h2 className="text-base font-semibold text-light-primary">Preferências de Cookies</h2>
                <p className="text-xs text-light-muted mt-0.5">Personalize quais cookies aceita</p>
              </div>
              <button
                onClick={() => setShowReview(false)}
                aria-label="Fechar preferências de cookies"
                className="text-light-muted hover:text-light-primary transition-colors p-1.5 rounded-full hover:bg-white/5"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12"/></svg>
              </button>
            </div>

            {/* Categories */}
            <div className="overflow-y-auto flex-1 px-6">
              <CategoryRow
                title="Cookies Essenciais"
                description="Necessários para o funcionamento básico do site — login de sessão, segurança e preferências de idioma. Não podem ser desativados."
                checked={true}
                disabled
              />
              <CategoryRow
                title="Cookies Analíticos"
                description="Nos ajudam a entender como os visitantes interagem com o site por meio de estatísticas anônimas (Google Analytics). As informações coletadas não identificam visitantes individualmente."
                checked={prefs.analytics}
                onChange={() => toggle('analytics')}
              />
              <CategoryRow
                title="Cookies de Marketing"
                description="Utilizados para exibir anúncios relevantes e medir o desempenho de campanhas publicitárias (Meta Pixel, Google Ads). Podem ser compartilhados com plataformas parceiras."
                checked={prefs.marketing}
                onChange={() => toggle('marketing')}
              />
              <CategoryRow
                title="Cookies de Personalização"
                description="Permitem que o site lembre suas preferências e ofereça uma experiência mais personalizada, como conteúdos recomendados com base no seu interesse."
                checked={prefs.personalization}
                onChange={() => toggle('personalization')}
              />
            </div>

            {/* Footer */}
            <div className="px-6 py-5 border-t border-white/8 shrink-0 flex flex-col sm:flex-row gap-2">
              <button
                onClick={savePrefs}
                className="flex-1 px-4 py-2.5 text-sm font-medium text-light-primary border border-white/20 rounded-xl hover:bg-white/5 transition-all"
              >
                Salvar preferências
              </button>
              <button
                onClick={acceptAll}
                className="flex-1 px-4 py-2.5 text-sm font-semibold bg-gold text-dark-base rounded-xl hover:bg-gold/90 transition-all"
              >
                Aceitar todos
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
